import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Box, Grid, Card, CardMedia, Typography } from '@mui/material';
import { IProjectCardDetails } from '../../types/project';
import { useSelector } from 'react-redux';
import { selectUser } from '../../features/auth/authSlice';
import env from '../../config/env';

const DARK_GREEN = '#2e453d';
const GOLD = '#CBBCA2';

const ProjectCard = (props: ChildProps) => {
  const user = useSelector(selectUser);
  const navigate = useNavigate();
  const { project } = props;
  const [imageError, setImageError] = useState(false);

  const showTasks = user.type !== 'Developer';
  const hasTasks = showTasks && project.taskCount > 0;

  const handleTasksClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    navigate('/tasks', { state: { project: { _id: project._id, name: project.name } } });
  };

  return (
    <Grid item xl={2.4} lg={2.4} md={4} sm={6} xs={12}>
      <Link to={`/${project._id}/dashboard`} style={{ textDecoration: 'none' }}>
        <Card
          elevation={0}
          sx={{
            height: '100%',
            width: '100%',
            border: hasTasks ? `1px solid ${DARK_GREEN}` : '1px solid #e2e2e2',
            borderTop: hasTasks ? `3px solid ${DARK_GREEN}` : '1px solid #e2e2e2',
            borderRadius: '6px',
            boxShadow: 'none',
            transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: '0 6px 16px rgba(0,0,0,0.08)',
            },
          }}
        >
          <Box sx={{ padding: '16px' }}>
            <Box
              sx={{
                height: 120,
                width: '100%',
                borderRadius: '4px',
                overflow: 'hidden',
                backgroundColor: '#f1f1ef',
              }}
            >
              {!imageError ? (
                <CardMedia
                  component="img"
                  sx={{ height: '100%', width: '100%', objectFit: 'cover' }}
                  image={`${env.basePath}/images/projects/${project._id}`}
                  title={project.name}
                  loading="lazy"
                  decoding="async"
                  onError={() => setImageError(true)}
                />
              ) : null}
            </Box>
            <Box sx={{ pt: 2 }}>
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: '1.05rem',
                  color: '#1a1a1a',
                  display: 'inline-block',
                  borderBottom: hasTasks ? `2px solid ${GOLD}` : 'none',
                  paddingBottom: hasTasks ? '4px' : 0,
                }}
              >
                {project.name}
              </Typography>
              <Box sx={{ mt: 1 }}>
                {showTasks ? (
                  hasTasks ? (
                    <Box
                      onClick={handleTasksClick}
                      sx={{ display: 'flex', alignItems: 'center', gap: '8px', width: 'fit-content', '&:hover': { textDecoration: 'underline' } }}
                    >
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          backgroundColor: DARK_GREEN,
                        }}
                      />
                      <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', color: DARK_GREEN }}>
                        {project.taskCount} open task{project.taskCount === 1 ? '' : 's'}
                      </Typography>
                    </Box>
                  ) : (
                    <Typography sx={{ fontSize: '0.9rem', color: '#a3a3a3' }}>No open tasks</Typography>
                  )
                ) : null}
              </Box>
            </Box>
          </Box>
        </Card>
      </Link>
    </Grid>
  );
};

interface ChildProps {
  project: IProjectCardDetails;
}

export default ProjectCard;
